import type {
  EPaperConnectionStatus,
  EPaperDevice,
} from "../../services/bluetooth/types"
import { normalizeMac12 } from "../../services/bluetooth/utils"
import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

export const E_BADGE_SCAN_STATUS = {
  IDLE: "idle",
  SCANNING: "scanning",
  ERROR: "error",
} as const

type EBadgeScanStatus =
  (typeof E_BADGE_SCAN_STATUS)[keyof typeof E_BADGE_SCAN_STATUS]

export const E_BADGE_BLE_BACKGROUND_STATUS = {
  IDLE: "idle",
} as const

export type EBadgeBleBackgroundStatus =
  | EPaperConnectionStatus
  | (typeof E_BADGE_BLE_BACKGROUND_STATUS)[keyof typeof E_BADGE_BLE_BACKGROUND_STATUS]

type EBadgeScanState = {
  status: EBadgeScanStatus
  backgroundStatus: EBadgeBleBackgroundStatus
  /** Prefetched BLE devices for badge steps; survives flow resets until explicitly cleared. */
  devices: EPaperDevice[]
  errorMessage: string | null
  /** Set when a scan session ends (timeout, endScan, etc.); survives flow resets for start-page prefetch throttling. */
  lastScanEndedAtMs: number | null
}

const initialState: EBadgeScanState = {
  status: E_BADGE_SCAN_STATUS.IDLE,
  backgroundStatus: E_BADGE_BLE_BACKGROUND_STATUS.IDLE,
  devices: [],
  errorMessage: null,
  lastScanEndedAtMs: null,
}

const eBadgeScanSlice = createSlice({
  name: "ebadgeScan",
  initialState,
  reducers: {
    eBadgeScanStarted(state) {
      state.status = E_BADGE_SCAN_STATUS.SCANNING
      state.errorMessage = null
    },
    eBadgeBleBackgroundStatusChanged(
      state,
      action: PayloadAction<EBadgeBleBackgroundStatus>,
    ) {
      state.backgroundStatus = action.payload
    },
    eBadgeScanDeviceFound(state, action: PayloadAction<EPaperDevice>) {
      const d = action.payload
      const existingIndex = state.devices.findIndex(
        (x) => x.deviceId === d.deviceId,
      )
      if (existingIndex >= 0) {
        state.devices[existingIndex] = {
          ...state.devices[existingIndex],
          ...d,
        }
      } else {
        state.devices.push(d)
      }

      state.devices.sort(
        (a, b) => (b.rssi ?? -Infinity) - (a.rssi ?? -Infinity),
      )
    },

    eBadgeScanFailed: {
      reducer(
        state,
        action: PayloadAction<{ message: string; endedAtMs: number }>,
      ) {
        state.status = E_BADGE_SCAN_STATUS.ERROR
        state.errorMessage = action.payload.message
        state.lastScanEndedAtMs = action.payload.endedAtMs
      },
      prepare: (message: string) => ({
        payload: { message, endedAtMs: Date.now() },
      }),
    },
    eBadgeScanEnded: {
      reducer(state, action: PayloadAction<number>) {
        state.status = E_BADGE_SCAN_STATUS.IDLE
        state.lastScanEndedAtMs = action.payload
      },
      prepare: () => ({ payload: Date.now() }),
    },
    eBadgeScanReset(state) {
      const { lastScanEndedAtMs, devices } = state
      return { ...initialState, lastScanEndedAtMs, devices }
    },
    eBadgeScanClearedDevices(state) {
      state.devices = []
    },
    eBadgeScanDeviceRemoved(state, action: PayloadAction<string>) {
      state.devices = state.devices.filter(
        (device) => normalizeMac12(device.name) !== action.payload,
      )
    },
  },
})

export const {
  eBadgeScanStarted,
  eBadgeScanDeviceFound,
  eBadgeScanDeviceRemoved,
  eBadgeBleBackgroundStatusChanged,
  eBadgeScanFailed,
  eBadgeScanEnded,
  eBadgeScanReset,
  eBadgeScanClearedDevices,
} = eBadgeScanSlice.actions

export const eBadgeScanReducer = eBadgeScanSlice.reducer
